import { FnaBuyAndSellParty, FNAParty } from '../../common/types/analyses';
import { GridCellParams } from '@mui/x-data-grid';
import { MIN_ESTATE_DUTY_RATE } from '../constants';
import { useCallback, useEffect, useState } from 'react';

const getRowId = (row: FNAParty): number => row.fnaPartyID;

const checkIfCellIsEditable = ({ row, field }: GridCellParams<FNAParty>): boolean => {
  // Allow editing of estate duty rate only if estate duty flag is enabled
  if (field === 'estatedutyRate') {
    return Boolean(row.isEstateduty);
  }
  return true;
};

/**
 * Build initial value for (suretyship, buy and sell, or keyperson) parties
 * @param parties Parties to build
 * @param addExtraFields Extra fields to the party
 */
export const buildFnaParties = <T = FNAParty | FnaBuyAndSellParty>(
  parties: FNAParty[],
  extraFields?: Partial<T>,
): T[] => {
  return parties.map(
    (party) =>
      ({
        fnaid: party.fnaid,
        fnaPartyID: party.id,
        roleplayerID: party.roleplayerID,
        partyRoleplayerIDReg: party.partyRoleplayerIDReg,
        partyRoleplayerTaxStatusNote: party.partyRoleplayerTaxStatusNote,
        partyRoleplayerID: party.partyRoleplayerID,
        partyRoleplayerName: party.partyRoleplayerName,
        representativeRoleplayerTypeID: party.representativeRoleplayerTypeID,
        representativeRoleplayerName: party.representativeRoleplayerName,
        ...extraFields,
      } as T),
  );
};

interface UseFnaPartiesUpdaterResult<T extends FNAParty> {
  /**
   * FNA parties that should be used to render the grid
   */
  parties: T[];
  /**
   * Data grid row update handler
   */
  processRowUpdate: (newRow: T) => T;
  /**
   * Helper function to get the row ID
   */
  getRowId: (row: T) => string | number;
  /**
   * Helper function to check if a cell is editable
   */
  checkIfCellIsEditable: ({ row, field }: GridCellParams<T>) => boolean;
  /**
   * Helper function to delete a party from party list
   */
  deleteParty: (row: T) => void;
}

/**
 * Hook to keep FNA parties in sync with the grid changes
 * @param fnaParties buy and sell / keyperson / suretyship FNA's parties
 */
export const useFnaPartiesUpdater = <T extends FNAParty>(
  fnaParties: T[] | undefined,
  forceEstateDuty?: boolean,
): UseFnaPartiesUpdaterResult<T> => {
  const setValue = useCallback(
    (party: T): T => {
      const isEstateduty = forceEstateDuty ? true : party.isEstateduty;
      return {
        ...party,
        isEstateduty,
        // Ensure that estate duty rate is present if estate duty flag is enabled
        estatedutyRate:
          isEstateduty && !party.estatedutyRate ? MIN_ESTATE_DUTY_RATE : party.estatedutyRate,
      };
    },
    [forceEstateDuty],
  );

  const [parties, setParties] = useState<T[]>(
    (fnaParties || []).map((element) => setValue(element)),
  );

  // Keep parties in sync with the grid changes
  const processRowUpdate = useCallback(
    (newRow: T): T => {
      setParties((currentParties) => {
        const updatedParties = [...currentParties];
        const index = updatedParties.findIndex(
          ({ fnaPartyID }) => fnaPartyID === newRow.fnaPartyID,
        );
        if (index !== -1) {
          updatedParties[index] = setValue({
            ...updatedParties[index],
            ...newRow,
          });
        }
        return updatedParties;
      });
      return newRow;
    },
    [setValue],
  );

  // Delete party
  const deleteParty = useCallback((row: T): void => {
    setParties((currentParties) => {
      return [...currentParties].filter(
        ({ partyRoleplayerID }) => partyRoleplayerID !== row.partyRoleplayerID,
      );
    });
  }, []);

  // Update parties when FNA changes
  useEffect(() => {
    setParties((fnaParties || []).map((element) => setValue(element)));
  }, [fnaParties, setValue]);

  return {
    parties,
    getRowId,
    processRowUpdate,
    checkIfCellIsEditable,
    deleteParty,
  };
};
